/**
 * `useListUrlState` (DNKADM-0430): a list screen's view state (tab, search, filters,
 * sort, page) kept in the URL query string, so a copied link or a reload reproduces the
 * same view.
 *
 * Usage: declare the screen's params and their defaults ONCE, as a module-level constant
 * (a fresh object each render would re-derive `values` and re-create `set` every render).
 * A computed default (e.g. "today minus 30 days") must also be stable: compute it once
 * (a module constant, or `useMemo(..., [])` / `useState` in the screen), never inline:
 *
 * ```ts
 * const URL_DEFAULTS = { tab: 'all', q: '', sort: '', dir: '', page: '1' } as const;
 * const { values, set } = useListUrlState(URL_DEFAULTS);
 * set({ q: next, page: null });           // one call per user action
 * ```
 *
 * The contract:
 * - Read: `values` holds every declared param. A param MISSING from the URL reads as its
 *   default; a param that is PRESENT reads as its raw value, even when empty (`?from=`
 *   reads `''`). Values are raw strings: the screen validates them (`parseSort`, an
 *   allow-list for a tab) so a hand-edited or stale link falls back instead of breaking.
 * - Write: `set(patch)` writes only the keys in `patch`. `null`/`undefined` or a value equal
 *   to the default REMOVES the param, so the default view keeps a clean `/path` link. Any
 *   other value is written, including `''` for a param whose default is not empty
 *   (`?from=`), so a user can clear a filter that has a non-empty default (the ledger's
 *   last-30-days range) and the cleared state survives a reload. Params the screen did not
 *   declare (another feature's deep link, e.g. `contactId`) are left untouched.
 * - History: `set` REPLACES the current entry by default, so typing in a search box never
 *   piles up Back-button entries. Pass `{ history: 'push' }` for a navigation the user
 *   should be able to step Back out of.
 * - Put every key one action changes in ONE `set` call (e.g. a new search also resets
 *   `page`). Two `set` calls in the same tick each start from the same URL, and the
 *   second wins.
 * - `page` in the URL is 1-indexed (it matches the "Page N" label). A screen whose state
 *   is 0-indexed converts at the edge.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/** A partial update: a new string value, or `null` to clear the param back to its default. */
export type ListUrlPatch<D extends Record<string, string>> = { [K in keyof D]?: string | null };

export interface ListUrlSetOptions {
  /** `replace` (default) rewrites the current history entry; `push` adds one. */
  history?: 'replace' | 'push';
}

export interface ListUrlState<D extends Record<string, string>> {
  /** Every declared param, defaults filled in. */
  values: { [K in keyof D]: string };
  /** Write some params; see the module doc for the contract. */
  set: (patch: ListUrlPatch<D>, options?: ListUrlSetOptions) => void;
}

/**
 * The largest page a URL may ask for. A hand-edited huge `page` would otherwise reach the
 * API as an index that overflows the server's int and leaves the list in an error state.
 */
export const MAX_URL_PAGE = 100_000;

/**
 * A 1-indexed URL `page` as a number. Only plain decimal digits from 1 to
 * {@link MAX_URL_PAGE} are accepted; anything else (exponent, hex, whitespace, a sign, a
 * huge number) reads as page 1.
 */
export function parsePage(raw: string | null | undefined): number {
  if (raw === null || raw === undefined || !/^\d+$/.test(raw)) return 1;
  const n = Number(raw);
  return Number.isSafeInteger(n) && n >= 1 && n <= MAX_URL_PAGE ? n : 1;
}

/** The URL `page` param for a 1-indexed page: `null` (cleared) for page 1. */
export function toPageParam(page: number): string | null {
  return page > 1 ? String(page) : null;
}

/** One value from an allow-list (a tab, a select filter); anything else reads as `fallback`. */
export function parseOption<T extends string>(
  raw: string | null | undefined,
  allowed: readonly T[],
  fallback: T,
): T {
  return allowed.find((option) => option === raw) ?? fallback;
}

export function useListUrlState<D extends Record<string, string>>(defaults: D): ListUrlState<D> {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.toString();

  const values = useMemo(() => {
    const params = new URLSearchParams(query);
    const out = {} as { [K in keyof D]: string };
    for (const key of Object.keys(defaults) as Array<keyof D & string>) {
      const raw = params.get(key);
      out[key] = raw === null ? (defaults[key] ?? '') : raw;
    }
    return out;
  }, [query, defaults]);

  const set = useCallback(
    (patch: ListUrlPatch<D>, options?: ListUrlSetOptions) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key of Object.keys(patch) as Array<keyof D & string>) {
            const value = patch[key];
            if (value === null || value === undefined || value === defaults[key]) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: options?.history !== 'push' },
      );
    },
    [setSearchParams, defaults],
  );

  return { values, set };
}
